// The portal a tab was launched from — e.g. an investor opening the scout dashboard from
// their profile menu. The destination portal reads it to offer "Return to <X> Dashboard".
//
// This is navigation origin, not identity, so it belongs to ONE tab and must die with it.
// It used to live in localStorage, which is browser-wide and permanent: closing the tab
// left the value behind, so a later direct login to scout still saw a stale "Return to
// Investor Dashboard", and two tabs hopping from different portals clobbered each other.
//
// It carries into the new tab because a same-origin tab opened via window.open() WITHOUT
// `noopener` inherits a copy of the opener's sessionStorage. Do not add `noopener` to the
// calls below. The ?from= param is the backstop for the cases where that copy never
// happens — popup blocked and the link opened by hand, or a pasted URL.

import storage, { sessionStore, SESSION_KEYS } from '../services/storage'

const ORIGIN_PARAM = 'from'

// The only origins any consumer acts on. Validating here keeps an arbitrary ?from= value
// out of both the rendered label and the portalRedirection call.
const VALID_ORIGINS = ['investor', 'startup']

/**
 * @param {*} origin - Candidate origin value.
 * @returns {boolean} True when it names a portal we can return to.
 */
export function isValidPortalOrigin(origin) {
  return VALID_ORIGINS.includes(origin)
}

/**
 * Record the portal this tab came from.
 * @param {string} origin - Portal name, e.g. 'investor'.
 */
export function setPortalOrigin(origin) {
  if (isValidPortalOrigin(origin)) {
    sessionStore.set(SESSION_KEYS.LOGIN_TYPE, origin)
  }
}

/**
 * The portal this tab came from.
 * @returns {string|null} Portal name, or null when this tab was not launched from one.
 */
export function getPortalOrigin() {
  const origin = sessionStore.get(SESSION_KEYS.LOGIN_TYPE)
  return isValidPortalOrigin(origin) ? origin : null
}

/**
 * Open another portal in a new tab, tagging it with the portal it was launched from.
 * @param {string} url - Destination path, e.g. '/scout/invite'.
 * @param {string} origin - Portal being left, i.e. `res.user.login_type`.
 */
export function openPortalTab(url, origin) {
  setPortalOrigin(origin)
  const target = isValidPortalOrigin(origin)
    ? `${url}${url.includes('?') ? '&' : '?'}${ORIGIN_PARAM}=${encodeURIComponent(origin)}`
    : url
  window.open(target, '_blank')
}

/**
 * Drop the localStorage copy this key used to live in. Nothing writes it any more, so
 * without this it sits in every existing user's browser forever. Safe to delete once
 * enough time has passed for active sessions to have run it.
 */
export function purgeLegacyPortalOrigin() {
  storage.remove(SESSION_KEYS.LOGIN_TYPE.key)
}

/**
 * Capture a ?from= origin off the landing URL and persist it for this tab. Called once on
 * app mount, because the user may navigate away from the landing URL before anything
 * reads the origin.
 * @param {object} location - A history location ({ pathname, search, hash }).
 * @returns {string|null} The URL to replace the current one with (the param stripped), or
 *   null when there was no origin param to consume.
 */
export function capturePortalOrigin(location) {
  if (!location || !location.search) {
    return null
  }
  const params = new URLSearchParams(location.search)
  const origin = params.get(ORIGIN_PARAM)
  if (!isValidPortalOrigin(origin)) {
    return null
  }
  setPortalOrigin(origin)
  params.delete(ORIGIN_PARAM)
  const search = params.toString()
  return `${location.pathname}${search ? `?${search}` : ''}${location.hash || ''}`
}
